import { useEffect, useState, useRef } from 'react'
import { useSyncStatus } from '../hooks/useSyncStatus'
import { Button } from '../../../shared/components/ui/button'
import { Callout } from '../../../shared/components/ui/callout'
import { CloudOffIcon, RefreshCwIcon, XIcon } from 'lucide-react'

/**
 * OfflineBanner component
 * - Persistent banner shown when offline
 * - Shows sync queue status
 * - Warns about offline refresh attempts
 * - Provides recovery UI for forced refresh
 */
export const OfflineBanner = () => {
  const { isOnline, pendingCount, sync, isSyncing } = useSyncStatus()

  // Initialize refresh warning state from localStorage
  const [showRefreshWarning, setShowRefreshWarning] = useState(() => {
    const offlineRefreshTimestamp = localStorage.getItem('offlineRefreshAttempted')
    if (offlineRefreshTimestamp) {
      const timestamp = parseInt(offlineRefreshTimestamp, 10)
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000
      // Clear the flag
      localStorage.removeItem('offlineRefreshAttempted')
      // Return true if refresh was recent (within 5 minutes)
      return timestamp > fiveMinutesAgo
    }
    return false
  })

  const [dismissed, setDismissed] = useState(false)

  // Reset dismissed state when connection is restored (without refresh warning)
  const prevOnline = useRef(isOnline)
  const prevShowRefreshWarning = useRef(showRefreshWarning)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOnline && !prevOnline.current && !showRefreshWarning) {
      setDismissed(false)
    }
    prevOnline.current = isOnline
    prevShowRefreshWarning.current = showRefreshWarning
  })

  const handleDismiss = () => {
    setDismissed(true)
  }

  const handleDismissRefreshWarning = () => {
    setShowRefreshWarning(false)
  }

  const handleSyncNow = () => {
    void sync()
    setShowRefreshWarning(false)
  }

  // Don't show if dismissed or online (unless there's a refresh warning)
  if ((dismissed && !showRefreshWarning) || (isOnline && !showRefreshWarning)) {
    return null
  }

  return (
    <>
      {/* Offline Refresh Warning - Critical */}
      {showRefreshWarning && (
        <Callout
          variant="danger"
          role="alert"
          className="fixed top-0 left-0 right-0 z-50 rounded-none shadow-lg"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-semibold">Page was refreshed while offline</p>
              <p className="mt-0.5">
                {isOnline
                  ? `You have ${pendingCount} pending changes. Sync now to prevent data loss.`
                  : 'You are still offline. Changes are queued for sync.'}
              </p>
            </div>
            <div className="flex gap-2">
              {isOnline && (
                <Button size="sm" variant="secondary" onClick={handleSyncNow} disabled={isSyncing}>
                  {isSyncing ? (
                    <>
                      <RefreshCwIcon className="mr-1 h-3 w-3 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCwIcon className="mr-1 h-3 w-3" />
                      Sync Now
                    </>
                  )}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={handleDismissRefreshWarning}
                aria-label="Dismiss"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Callout>
      )}

      {/* Offline Mode Banner */}
      {!isOnline && !dismissed && (
        <Callout
          variant="warning"
          icon={CloudOffIcon}
          role="status"
          className="fixed top-0 left-0 right-0 z-50 rounded-none shadow-lg"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-semibold">Working Offline</p>
              <p className="mt-0.5">
                {pendingCount > 0
                  ? `${pendingCount} ${pendingCount === 1 ? 'change' : 'changes'} will sync when connection is restored.`
                  : 'Changes will sync automatically when connection is restored.'}
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={handleDismiss} aria-label="Dismiss">
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
        </Callout>
      )}
    </>
  )
}
